if (!customElements.get('sth-size-check')) {
  customElements.define('sth-size-check', class extends HTMLElement {
    connectedCallback() {
      if (this.sthInitialized) return;
      this.sthInitialized = true;
      this.dialog = this.querySelector('.size-check__dialog');
      this.form = this.querySelector('form');
      this.result = this.querySelector('.size-check__result');
      this.config = JSON.parse(this.querySelector('[data-size-check-config]').textContent);
      this.trigger = this.querySelector('.size-check__trigger');
      this.sizeChart = this.parseSizeChart();
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeDialog();
      });
      document.body.append(this.dialog);
      this.trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.openDialog();
      });
      ['click', 'input', 'change', 'submit'].forEach((type) => {
        this.dialog.addEventListener(type, (event) => event.stopPropagation());
      });
      this.dialog.querySelector('.size-check__close').addEventListener('click', () => this.closeDialog());
      this.form.elements.units.addEventListener('change', () => this.changeUnits(this.form.elements.units.value));
      this.form.addEventListener('submit', (event) => this.recommend(event));
      this.changeUnits('metric');
      this.dialog.querySelector('.size-check__restart').addEventListener('click', () => this.restart());
    }

    openDialog() {
      this.form.elements.units.value = this.currentUnits || 'metric';
      this.scrollPosition = window.scrollY;
      this.bodyStyles = {
        overflow: document.body.style.overflow,
        position: document.body.style.position,
        top: document.body.style.top,
        width: document.body.style.width
      };
      this.syncVisualViewport = () => {
        const viewport = window.visualViewport;
        const viewportHeight = viewport?.height || window.innerHeight;
        this.dialog.style.setProperty('--size-check-viewport-height', `${viewportHeight}px`);
        window.requestAnimationFrame(() => {
          const focusedControl = this.dialog.querySelector('input:focus, select:focus');
          focusedControl?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        });
      };
      this.syncVisualViewport();
      window.visualViewport?.addEventListener('resize', this.syncVisualViewport);
      window.visualViewport?.addEventListener('scroll', this.syncVisualViewport);
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.width = '100%';
      this.dialog.hidden = false;
      this.dialog.querySelector('.size-check__panel').scrollTop = 0;
      this.dialog.querySelector('.size-check__close').focus();
    }

    closeDialog() {
      this.dialog.hidden = true;
      window.visualViewport?.removeEventListener('resize', this.syncVisualViewport);
      window.visualViewport?.removeEventListener('scroll', this.syncVisualViewport);
      this.dialog.style.removeProperty('--size-check-viewport-height');
      this.dialog.style.removeProperty('--size-check-viewport-width');
      this.dialog.style.removeProperty('--size-check-viewport-top');
      this.dialog.style.removeProperty('--size-check-viewport-left');
      document.body.style.overflow = this.bodyStyles?.overflow || '';
      document.body.style.position = this.bodyStyles?.position || '';
      document.body.style.top = this.bodyStyles?.top || '';
      document.body.style.width = this.bodyStyles?.width || '';
      window.scrollTo(0, this.scrollPosition || 0);
      this.trigger.focus();
    }

    changeUnits(units) {
      const old = this.currentUnits || 'metric';
      const metric = units === 'metric';
      if (old !== units) {
        const fields = this.form.elements;
        if (!metric) {
          if (fields.heightCm.value) {
            const inches = Number(fields.heightCm.value) / 2.54;
            fields.heightFt.value = Math.floor(inches / 12);
            fields.heightIn.value = (inches % 12).toFixed(2);
          }
          if (fields.weightKg.value) fields.weightLb.value = (Number(fields.weightKg.value) / 0.45359237).toFixed(2);
        } else {
          if (fields.heightFt.value) fields.heightCm.value = ((Number(fields.heightFt.value) * 12 + Number(fields.heightIn.value || 0)) * 2.54).toFixed(2);
          if (fields.weightLb.value) fields.weightKg.value = (Number(fields.weightLb.value) * 0.45359237).toFixed(2);
        }
        this.form.querySelectorAll('[data-body-measurement]').forEach(input => {
          const value = input.value.trim().replace(',', '.');
          if (value && Number.isFinite(Number(value))) input.value = (Number(value) * (metric ? 2.54 : 1 / 2.54)).toFixed(2);
        });
      }
      this.currentUnits = units;
      this.form.elements.units.value = units;
      this.dialog.querySelector('.size-check__metric-inputs').hidden = !metric;
      this.dialog.querySelector('.size-check__imperial-inputs').hidden = metric;
      this.dialog.querySelectorAll('.size-check__metric-inputs input').forEach(input => { input.disabled = !metric; input.required = metric; });
      this.dialog.querySelectorAll('.size-check__imperial-inputs input').forEach(input => { input.disabled = metric; input.required = !metric && input.name !== 'heightIn'; });
      this.dialog.querySelectorAll('[data-body-unit]').forEach(unit => { unit.textContent = metric ? 'cm' : 'in'; });
      this.form.querySelectorAll('[data-body-measurement]').forEach(input => {
        input.placeholder = metric ? input.dataset.cmPlaceholder : input.dataset.inPlaceholder;
      });
    }

    normalizeSize(value) {
      return window.SthSizeCheckEngine.normalizeSize(value);
    }

    message(key, replacements = {}) {
      let value = this.config.messages[key] || key;
      Object.entries(replacements).forEach(([name, text]) => { value = value.replaceAll(`{${name}}`, text); });
      return value;
    }

    parseSizeChart() {
      const engine = window.SthSizeCheckEngine;
      const base = engine.parseChart(engine.richText(this.config.chartData), this.config.sizes);
      const variantId = document.querySelector(`#product-form-${this.dataset.sectionId} input[name="id"]`)?.value;
      const variants = this.config.variants || [];
      const selected = variants.find(v => String(v.id) === variantId) || variants.find(v => String(v.id) === String(this.config.selectedVariant));
      if (selected) {
        variants.filter(v => v.options.every((option, i) => i === this.config.sizePosition - 1 || option === selected.options[i])).forEach(v => {
          if (!v.chart) return;
          const size = engine.normalizeSize(v.options[this.config.sizePosition - 1]);
          const row = engine.parseChart(`${size}\n${engine.richText(v.chart)}`, [size])[size] || {};
          base[size] ||= {};
          Object.entries(row).forEach(([field, cell]) => {
            const original = base[size][field];
            if (original && Math.abs(original.min - cell.min) <= 0.6 && Math.abs(original.max - cell.max) <= 0.6) return;
            base[size][field] = cell;
          });
        });
      }
      return base;
    }

    recommend(event) {
      event.preventDefault();
      const error = this.dialog.querySelector('.size-check__error');
      if (!this.form.reportValidity()) return;
      error.hidden = true;
      const data = new FormData(this.form);
      const imperial = (data.get('units') || this.currentUnits || 'metric') === 'imperial';
      const measurement = name => {
        const value = String(data.get(name) || '').trim().replace(',', '.');
        return value ? Number(value) * (imperial ? 2.54 : 1) : null;
      };
      this.sizeChart = this.parseSizeChart();
      const outcome = window.SthSizeCheckEngine.recommend(this.config, this.sizeChart, {
        height: imperial ? (Number(data.get('heightFt')) * 12 + Number(data.get('heightIn') || 0)) * 2.54 : Number(data.get('heightCm')),
        weight: Number(data.get(imperial ? 'weightLb' : 'weightKg')) * (imperial ? 0.45359237 : 1),
        chest: measurement('chest'), waist: measurement('waist'), hip: measurement('hip'), maxLength: measurement('maxLength'),
        build: data.get('build'), usualSize: data.get('usualSize'), preferredFit: data.get('preferredFit')
      });
      if (outcome.status === 'input') {
        error.textContent = this.message(outcome.reason);
        error.hidden = false;
        if (outcome.field) { const field = this.form.elements[outcome.field]; const details = field?.closest('details'); if (details) details.open = true; field?.focus(); }
        return;
      }
      this.showOutcome(outcome);
    }

    showOutcome(outcome) {
      const title = this.dialog.querySelector('.size-check__result-title');
      const copy = this.dialog.querySelector('.size-check__result-copy');
      const actions = this.dialog.querySelector('.size-check__result-actions');
      actions.replaceChildren();
      if (outcome.status === 'match') {
        const size = outcome.sizes[0];
        title.textContent = this.message('size_title', { size });
        copy.textContent = this.message(outcome.estimated ? 'estimated_copy' : 'match_copy', { size, length: `${Number(outcome.length.toFixed(1))} cm / ${(outcome.length / 2.54).toFixed(1)} in` });
        if (outcome.lengthEstimated) copy.textContent += ' ' + this.message('coat_copy');
        if (outcome.sleeveUnverified) copy.textContent += ' ' + this.message('sleeve_copy');
        if (outcome.fit === 'oversized') copy.textContent += ' ' + this.message('oversized_copy');
        if (outcome.runsSmall) copy.textContent += ' ' + this.message('small_fit_copy');
        if (outcome.unverifiedHip) copy.textContent += ' ' + this.message('hip_unverified');
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'button';
        button.textContent = this.message('select_size', { size });
        button.addEventListener('click', () => this.selectSize(size));
        actions.append(button);
      } else {
        title.textContent = this.message(outcome.status === 'no_match' ? 'no_match_title' : 'review_title');
        copy.textContent = this.message(outcome.reason);
      }
      this.form.hidden = true;
      this.result.hidden = false;
      title.tabIndex = -1;
      title.focus();
    }

    selectSize(size) {
      const fieldset = document.getElementById(`SizeSelector-${this.dataset.sectionId}`);
      const options = fieldset ? [...fieldset.querySelectorAll('input[type="radio"], option')] : [];
      const target = options.find((option) => this.normalizeSize(option.value) === this.normalizeSize(size));
      if (target) {
        if (target.tagName === 'OPTION') {
          target.parentElement.value = target.value;
          target.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          target.click();
        }
      }
      this.closeDialog();
    }

    restart() {
      this.result.hidden = true;
      this.form.hidden = false;
    }
  });
}

